/**
 * Minimal CSV parser tailored to the import format:
 *   - Comma-separated
 *   - Double-quote escapes (CSV standard)
 *   - First line is the header
 *   - Array columns use pipe (|) as the inner delimiter
 *
 * Not a general-purpose CSV library — papaparse is the right call if/when we
 * need stream parsing, type inference, or other CSV dialects.
 */

export function parseCSV(text: string): { header: string[]; rows: Record<string, string>[] } {
  // \uFEFF is Excel's UTF-8 BOM, written as an escape rather than as a literal:
  // an invisible character in source is unreadable, un-greppable, and trips
  // ESLint's no-irregular-whitespace rule (which was failing CI).
  const lines = text
    .replace(/^\uFEFF/, '')
    .trim()
    .split(/\r?\n/);
  if (lines.length === 0) return { header: [], rows: [] };

  const header = splitCsvLine(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    header.forEach((col, idx) => {
      row[col] = cells[idx] ?? '';
    });
    rows.push(row);
  }

  return { header, rows };
}

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  out.push(current);
  return out;
}

/**
 * Map a parsed Nouns/DerDieDas CSV row into the shape the import API expects.
 */
export function csvRowToImportRow(row: Record<string, string>) {
  return {
    de: (row.de ?? '').trim(),
    article: row.article ? (row.article.trim() as 'der' | 'die' | 'das') : undefined,
    plural: row.plural?.trim() || undefined,
    en: splitPipe(row.en),
    pt: splitPipe(row.pt),
    example: row.example?.trim() || null,
    examplePt: row.examplePt?.trim() || null,
    level: row.level?.trim() || null,
    difficulty: row.difficulty ? clamp1to3(Number(row.difficulty)) : 2,
    falseFriend: parseBool(row.falseFriend),
    warning: row.warning?.trim() || undefined,
  };
}

function splitPipe(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split('|')
    .map((v) => v.trim())
    .filter(Boolean);
}

function parseBool(value: string | undefined): boolean {
  if (!value) return false;
  return /^(true|1|yes|y)$/i.test(value.trim());
}

function clamp1to3(n: number): 1 | 2 | 3 {
  if (n <= 1) return 1;
  if (n >= 3) return 3;
  return 2;
}
